/**
 * Unified types for Grounding Search Visualization
 *
 * These types standardize the data structure for grounding search data
 * across different parts of the application.
 */

// Rank band thresholds and colors based on the spec
export type RankBand = "top3" | "top10" | "top20" | "top30" | "unranked";

export interface RankBandConfig {
	label: string;
	min: number;
	max: number | null;
	color: string;
	bgColor: string;
	borderColor: string;
}

export const RANK_BANDS: Record<RankBand, RankBandConfig> = {
	top3: {
		label: "Top 3",
		min: 1,
		max: 3,
		color: "text-green-800",
		bgColor: "bg-green-100",
		borderColor: "border-green-200",
	},
	top10: {
		label: "4-10",
		min: 4,
		max: 10,
		color: "text-yellow-800",
		bgColor: "bg-yellow-100",
		borderColor: "border-yellow-200",
	},
	top20: {
		label: "11-20",
		min: 11,
		max: 20,
		color: "text-orange-800",
		bgColor: "bg-orange-100",
		borderColor: "border-orange-200",
	},
	top30: {
		label: "21-30",
		min: 21,
		max: 30,
		color: "text-red-800",
		bgColor: "bg-red-100",
		borderColor: "border-red-200",
	},
	unranked: {
		label: "Not Ranked",
		min: 31,
		max: null,
		color: "text-gray-600",
		bgColor: "bg-gray-100",
		borderColor: "border-gray-200",
	},
};

// Search engine types
export type SearchEngine = "google" | "brave";

// Unified discovery query reference
export interface DiscoveryQueryRef {
	id: string;
	query: string;
	queryType?: string;
}

// Unified competitor ranking
export interface CompetitorRanking {
	competitorId: string;
	competitorName: string;
	competitorDomain: string;
	rankGoogle: number | null;
	urlGoogle: string | null;
	rankBrave: number | null;
	urlBrave: string | null;
}

// Unified grounding search item
export interface GroundingSearchItem {
	id: string;
	searchQuery: string;
	usageCount: number;
	discoveryQueries: DiscoveryQueryRef[];
	/** Platforms that produced this grounding search (e.g. openai, gemini, claude). */
	platforms?: string[];
	/** True when Google SERP didn't run (Claude-only search); show Google as N/A. */
	googleUnavailable?: boolean;
	/** True when Brave SERP didn't run (no Claude platform); show Brave as N/A. */
	braveUnavailable?: boolean;
	/** True when Brave search was skipped due to rate limiting error. */
	braveError?: boolean;
	brandRankGoogle: number | null;
	brandUrlGoogle: string | null;
	brandRankBrave: number | null;
	brandUrlBrave: string | null;
	competitorRankings: CompetitorRanking[];
	isGapGoogle: boolean;
	isGapBrave: boolean;
	isGapBoth: boolean;
	impactScore: number;
	/**
	 * The domain a `site:`-scoped search pointed at (e.g. "reddit.com"), else
	 * undefined. Present means the model told itself which site to read instead of
	 * searching the open web — so the row is evidence about authority, never a
	 * ranking opportunity. Its rank cells read N/A, its gap flags are false and
	 * its impact is 0, because no SERP task was ever run for it.
	 */
	siteScopeDomain?: string | null;
	/** Whose site that was, from this property's point of view. */
	siteScopeClass?: "own" | "competitor" | "third_party" | null;
	/** Competitor display name when `siteScopeClass` is "competitor". */
	siteScopeCompetitorName?: string | null;
}

// Summary KPI data
export interface GroundingSearchKPIs {
	totalSearches: number;
	totalGaps: number;
	rankedSearches: number;
	highImpactGaps: number;
	avgImpactScore: number;
	categoryCoverage?: number;
}

// Filter state
export interface GroundingSearchFilters {
	searchTerm: string;
	filterBy: "all" | "gaps" | "ranked";
	sortBy: "impact" | "usage" | "competitors" | "query";
	searchEngine: "all" | "google" | "brave";
	/** Scope by platform that generated the search (e.g. openai, gemini). */
	platformFilter: string;
	selectedCompetitor: string | null;
	rankBandFilter: RankBand | "all";
	/**
	 * Show `site:`-scoped searches. Defaults to FALSE everywhere: they can never
	 * rank and can never be optimized, so leaving them in the default list would
	 * pad every count with rows nobody can act on. Surfaces opt in explicitly and
	 * label what they're showing.
	 */
	showSiteScoped: boolean;
}

// View mode
export type ViewMode = "matrix" | "list" | "compact";

// Component props interfaces
export interface GroundingSearchVisualizationProps {
	data: GroundingSearchItem[];
	kpis?: GroundingSearchKPIs;
	showKPIHeader?: boolean;
	showFilters?: boolean;
	showDetailDrawer?: boolean;
	viewMode?: ViewMode;
	maxItems?: number;
	isLoading?: boolean;
	isUnlocked?: boolean;
	accentColor?: string;
	onItemClick?: (item: GroundingSearchItem) => void;
	onDiscoveryQueryClick?: (queryId: string) => void;
	organizationSlug?: string;
	propertyId?: string;
	className?: string;
	emptyStateMessage?: string;
}

export interface GroundingSearchKPIHeaderProps {
	kpis: GroundingSearchKPIs;
	className?: string;
}

export interface GroundingSearchTableProps {
	items: GroundingSearchItem[];
	viewMode: ViewMode;
	selectedItem?: GroundingSearchItem | null;
	onItemClick?: (item: GroundingSearchItem) => void;
	onDiscoveryQueryClick?: (queryId: string) => void;
	organizationSlug?: string;
	propertyId?: string;
	maxItems?: number;
	showExpandButton?: boolean;
}

export interface GroundingSearchDetailDrawerProps {
	item: GroundingSearchItem | null;
	isOpen: boolean;
	onClose: () => void;
	onDiscoveryQueryClick?: (queryId: string) => void;
	organizationSlug?: string;
	propertyId?: string;
}

export interface GroundingSearchFiltersProps {
	filters: GroundingSearchFilters;
	onFiltersChange: (filters: GroundingSearchFilters) => void;
	competitors: Array<{ id: string; name: string; domain: string }>;
	showEngineFilter?: boolean;
	showRankBandFilter?: boolean;
	className?: string;
}
